/* eslint-disable max-len */
import React from 'react'
import Link from 'root/src/client/web/base/Link'
import {
	PRIVACY_POLICY_ROUTE_ID,
	COOKIE_POLICY_ROUTE_ID,
	COPYRIGHT_POLICY_ROUTE_ID,
	RULES_OF_USE_ROUTE_ID,
} from 'root/src/shared/descriptions/routes/routeIds'
import {
	EMAIL,
} from 'root/src/shared/constants/pageData'
import Anchor from 'root/src/client/web/base/Anchor'

const pageContent = classes => [
	{
		title: '1. Welcome to Dare Drop!',
		paragraph: (
			<span>
				This page explains our terms of service. When you use Dare Drop, you’re
				agreeing to all the rules on this page. We’ve done our best to offer
				you clear and simple explanations of what everything means with brief
				summaries before each Section. The summaries are not part of the
				official legal terms.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					Welcome to Dare Drop. By using this website (the “Site”) and services (together with the
					Site, the “Services”) offered by Dare Drop (together with its parents, subsidiaries,
					affiliates, agents, representatives, consultants, employees, officers, and directors —
					collectively, “Dare Drop,” “we,” or “us”), and used by companies marketing their products
					and services (”Brands”), you’re agreeing to these legally binding rules
					(the “Terms”). You’re also agreeing to our{' '}
					<Link routeId={PRIVACY_POLICY_ROUTE_ID}>Privacy Policy</Link>,{' '}
					<Link routeId={COOKIE_POLICY_ROUTE_ID}>Cookie Policy</Link>, and{' '}
					<Link routeId={COPYRIGHT_POLICY_ROUTE_ID}>Copyright Policy</Link>;
					and agreeing to follow any other rules on the Site, like our{' '}
					<Link routeId={RULES_OF_USE_ROUTE_ID}>Rules of Use</Link>.
				</p>
				<p>
					We may change these terms from time to time. If we do, we’ll let you know about any
					material changes, either by notifying you on the Site or by sending you an email. New
					versions of the terms will never apply retroactively and we’ll tell you the exact date
					they go into effect. If you keep using Dare Drop after a change, that means you accept
					the new terms.
				</p>
				<p>
					Dare Drop is for your personal, non-commercial use, except as explained in{' '}
					<Anchor anchor="section-4">Section 4</Anchor>
					{' '}and{' '}
					<Anchor anchor="section-5">Section 5</Anchor>{' '}
					below.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '2. Creating an Account',
		paragraph: (
			<span>
				To use Dare Drop, you need to be 13 years old or older.
				If you’re between 13 and 18 (or the legal age of majority for your
				jurisdiction), you may only use Dare Drop under the supervision of a
				parent or legal guardian who agrees to these Terms. You’re responsible
				for your account and all the activity on it.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					You can browse Dare Drop without registering for an account. But to
					use some of Dare Drop’s functions, you’ll need to register. When you
					do that, the information you give us has to be accurate and complete.
					Don’t choose names that are offensive or that violate anyone’s
					rights, and don’t impersonate anyone. If you don’t follow these
					rules, we may cancel your account.
				</p>
				<p>
					You’re responsible for all the activity on your account, and for keeping your password
					confidential. If you find out that someone has used your account without your permission,
					report it to <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
				</p>
				<p>
					To sign up for an account, you need to be at least 18 years old, old
					enough to form a binding contract where you live, or at least 13 years
					old under the supervision of a parent or guardian who agrees to be
					bound by these Terms of Service. If necessary, we may ask you for proof
					of your age.
				</p>
				<p>
					When creating an account you often will connect social channels like YouTube.
					When you do, you are agreeing to be bound by their terms. In YouTube's case,
					that's <a href="https://www.youtube.com/t/terms">YouTube Terms of Service</a>.
					You can also revoke Dare Drop's access to your Google account via
					the <a href="https://security.google.com/settings/security/permissions">
						Google security	settings page
						{/* eslint-disable-next-line */}
         </a>.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '3. Don’t Do These Things',
		paragraph: (
			<span>
				This section is a list of things you know you shouldn’t do: lie, break laws, abuse people,
				steal data, hack other people’s computers, and so on. If you do any of these things, you
				may be removed from the site and lose claim to any Digital Content, rewards, or compensation
				previously earned. Please behave yourself.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					Many people use Dare Drop. We expect all of them to help keep this a nice place. Don’t do
					any of these things on the Site:
				</p>
				<ul className={classes.termsList}>
					<li>
						<strong>Don’t break the law.</strong> Don’t take any action that infringes or violates
						other people’s rights, violates the law, or breaches any contract or legal duty you have
						toward anyone
					</li>
					<li>
						<strong>Don’t spam.</strong> Don’t distribute unsolicited or unauthorized advertising or
						promotional material, or any junk mail, spam, or chain letters. Don’t run mail
						TermLists, TermListservs, or any kind of auto-responder or spam on or through the Site.
					</li>
					<li>
						<strong>Don’t lie to people.</strong> Don’t post information you know is false,
						misleading, or inaccurate. Don’t do anything deceptive or fraudulent.
					</li>
					<li>
						<strong>Don’t solicit use of prohibited items of activities. </strong>
						Don’t post Dares that include or encourage illegal actions or items, violate any of
						Dare Drop’s policies, rules, or guidelines, or violate any applicable law, statute,
						ordinance, or regulation.
					</li>
					<li>
						<strong> Don’t victimize anyone.</strong> Don’t do anything threatening, abusive,
						harassing, defamatory, libelous, tortious, obscene, profane, or invasive of another
						person’s privacy.
					</li>
					<li>
						<strong> Don’t harm anyone’s computer. </strong>Don’t distribute software viruses, or
						anything else (code, films, programs) designed to interfere with the proper function of
						any software, hardware, or equipment on the Site (whether it belongs to Dare Drop or
						another party).
					</li>
					<li>
						<strong> Don’t buy views, viewbot, or manipulate data in linked sites. </strong> Don’t
						do anything that undermines the integrity of data linked to the Site, or that manipulates
						the viewership of content through automated or inorganic means.
					</li>
					<li>
						<strong> Don’t resell keys or Rewards. </strong>Don’t sell or exchange any of the keys
						or Rewards obtained through Dare Drop.
					</li>
				</ul>
				<p>
					We also need to make sure that the Site is secure and functions properly. So don’t do any
					of these things — most of which boil down to “don’t mess with our system.”
				</p>
				<ul className={classes.termsList}>
					<li>Don’t try to interfere with the proper workings of the Services.</li>
					<li>Don’t bypass any measures we’ve put in place to secure the Services.</li>
					<li>
						Don’t try to damage or get unauthorized access to any system, data, password, or other
						information, whether it belongs to Dare Drop or another party.
					</li>
					<li>
						Don’t take any action that imposes an unreasonable load on our infrastructure, or on our
						third-party providers. (We reserve the right to determine what’s reasonable.)
					</li>
					<li>
						Don’t use any kind of software or device (whether it’s manual or automated) to “crawl”
						or “spider” any part of the Site.
					</li>
					<li>
						Don’t take apart or reverse engineer any aspect of Dare Drop in an effort to access
						things like source code, underlying ideas, or algorithms.
					</li>
				</ul>
			</React.Fragment>
		),
	},
	{
		id: 'section-4',
		title: '4. How Dares, Drops, and Campaigns Work',
		paragraph: (
			<span>
				Most of our Terms of Service explain your relationship with Dare Drop. This section is
				different — it explains the relationship between creators and backers of Dare Drop Dares,
				Drops, and Campaigns; and who’s responsible for what. This is what you’re agreeing to
				when you create, fund, or deliver results for a Dare Drop Dare, Drop, or Campaign.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					Dare Drop provides a funding platform for creative Dares, inside, or outside a Drop
					and Campaign. When a creator fulfills a Dare on Dare Drop or a funder backs a Dare
					on Dare Drop inside or outside a Drop or Campaign, they’re inviting other people to
					form a contract with them. Anyone who delivers the result for a Dare is accepting
					the creator’s and funder’s offer, and forming that contract.
				</p>
				<p>
					Dare Drop is not a part of this contract — the contract is a direct legal agreement
					between creators, funders, and deliverers. Here are the terms that govern that agreement:
				</p>
				<p>
					If a deliverer recieves anything to create content, they need to clearly note that
					for their audience. An indication in the title of the post or stream that it is an
					"#AD", "#daredrop" or "#SPONSORED" will suffice.
				</p>
				<p>
					When a Dare is successfully completed on the deliverer’s channel,{' '}
					<b>the funders must pay their pledged amounts to the deliverer</b>. Once a funder
					has done so, they’ve satisfied their obligation to the other funders and the
					deliverer. In some cases, Drops and Campaigns, the funder supplies a set of rewards
					for all deliverers. This set may not be replenished and the Dare, along with the
					associated contract, will be nullified when the relevant rewards for it have run out.
				</p>
				<p>
					Throughout the process, deliverers owe their funders a high standard of effort, honest
					communication, and a dedication to bringing the Dare to life. At the same time, funders
					must understand that when they fund a Dare, they’re helping to create something. There
					may be changes or delays, and there’s a chance something could happen that prevents a
					deliverer from being able to finish the Dare as requested.
				</p>
				<p>
					The deliverers are each solely responsible for fulfilling the promises made in their
					claimed Dare. If they’re unable to satisfy the terms of this agreement, they may be
					subject to legal action by the funders.
				</p>
				<p>
					When rewards are listed on Dares, those rewards are to be given to the deliverer's
					audience through some manner of random giveaway unless explicitly stated otherwise.
				</p>
				<p>
					When you deliver a dare, you're obligated to leave the clip for that dare up for 30 days,
					or as long as the platform you created it on supports - whichever is shorter.
				</p>
			</React.Fragment>
		),
	},
	{
		id: 'section-5',
		title: '5. How Funding Works',
		paragraph: (
			<span>
				This section goes over the details of funding and creating Dares. This is how money gets
				collected, and the details on how and when pledges can be changed or canceled.
			</span>

		),
		text: (
			<React.Fragment>
				<p>These are the terms that apply when you’re funding or creating a Dare:</p>
				<ul className={classes.termsList}>
					<li>
						You’re only charged if the Dare is completed. You’ll provide your payment information
						when you pledge, but you won’t be charged. Your payment will only be collected if, at
						the time of the Dare’s funding deadline, the Dare has been completed. The exact
						amount you pledged is the amount Dare Drop will collect. If the Dare hasn’t been
						completed, you won’t be charged, and no money will change hands.
					</li>
					<li>
						In some cases we’ll reserve the charge on your card. Dare Drop and its payment partners
						may authorize or reserve a charge on your credit card (or whatever payment method you
						use) for any amount up to the full pledge, at any time between the pledge and the
						collection of funds.
					</li>
					<li>
						You can increase, decrease, or cancel your pledge at any time
						during the Dare, with one exception. During the last 24 hours before the Dare is
						completed, you can’t decrease or cancel your pledge without contacting customer
						support first — if the deliverer has already created the video or is in the process of
						streaming the video which will be used to deliver on the Dare, then you may not cancel
						or decrease your pledge.
					</li>
					<li>
						Dare Drop doesn’t offer refunds. Responsibility for finishing a Dare lies entirely
						with the Dare deliverer. Dare Drop doesn’t hold funds on deliverers’ behalf, cannot
						guarantee deliverers’ work, and does not offer refunds.
					</li>
				</ul>
				<p>
					These are the terms that apply when you’re <b>delivering a Dare</b>:
				</p>
				<ul className={classes.termsList}>
					<li>
						<b>We’ll charge our fees before putting funds in your account</b>. Dare Drop and its
						payment partners will subtract fees before transmitting the proceeds of a Dare.
					</li>
					<li>
						<b>Some pledges can’t be collected, which might reduce the amount of funding you get</b>
						. Because some payments can’t be collected — for instance, when a backer’s credit card
						expires before funding ends, and they don’t provide updated information — we can’t
						guarantee that the amount of funding you receive will be exactly equal to the full
						amount pledged minus fees.
					</li>
					<li>
						<b>We’ll help resolve payment-card disputes</b>. If a backer of your Dare disputes
						the charge with their card issuer, we’ll handle re-presenting the charge to settle the
						dispute with the card issuer. You’ll be notified that a dispute has been filed, and
						you’ll be able to provide evidence to help us resolve it in your favor. If the
						cardholder’s dispute is found valid, you authorize us to charge the credit card number
						you provided when you completed the Dare for the amount of the chargeback.
					</li>
					<li>
						<b>Don’t count your chickens before they hatch</b>. Don’t assume you’ll be able to
						deliver on a Dare when you want; there could be a reason we’re not able to accept it,
						or a problem that takes time to resolve. Don’t assume you’ll be able to immediately
						collect your funding; there may be a delay between the end of a successful Dare and
						your access to the funds. And don’t take any actions in reliance on collecting any of
						the money pledged until you actually have the ability to withdraw it from your account
						and spend it.
					</li>
				</ul>
			</React.Fragment>
		),
	},
	{
		id: 'section-6',
		title: '6. How Points and Rewards Work',
		paragraph: (
			<span>
				This Section outlines what Digital Content like Points and Rewards are, and are not. In short,
				Dare Drop may offer Digital Content to Users who contribute to our Community. This Digital
				Content has no monetary value, and is not a currency. Dare Drop does not guarantee that
				Digital Content will be available at all times or at any given time.
			</span>

		),
		text: (
			<React.Fragment>
				<p>The Ancillary Products and Services that Dare Drop may offer, on a promotional
					basis or as a result of actions on Dare Drop, may include Points (“Points”),
					which are digital content that allow Dare Drop to express appreciation for
					specific Users who use Dare Drop. The Ancillary Products and Services may also
					include certain rewards or benefits (“Rewards”) provided to Users.
				</p>
				<p>Collectively, Points and Rewards will be called “Digital Content.”</p>
				<p>
					Digital Content has no monetary value (i.e., is not cash or equivalent), and
					does not constitute currency or property of any type. Digital Content cannot be
					sold or transferred to third parties, including other Users, cannot be used
					outside the Dare Drop Services, and cannot be exchanged for cash or for any
					other goods or services. Users have no property, proprietary, intellectual
					property, ownership, or monetary interest in promotional or purchased Digital
					Content, which remains Dare Drop Digital Content. Except as required by
					applicable law in your jurisdiction, Digital Content is non-refundable. Users
					may not obtain a refund for Points that are used or redeemed. You may not
					purchase, sell, barter, or trade any Digital Content, or offer to purchase,
					sell, or trade any Digital Content (including for money or any other
					consideration or items of value whether inside or outside of the Dare Drop
					Services). Dare Drop and Users recognize that any such attempted transfer will
					be null and void.
				</p>
				<p>
					Although Dare Drop may share revenue generated from the sale of Digital Content
					with a User receiving the Digital Content, subject to the agreement solely
					between Dare Drop and the User as the case may be, Digital Content is not
					intended to be used as a means to provide compensation or financial support to
					the User, and Dare Drop makes no assurances to any User that anyone receiving
					Digital Content will receive a financial benefit.
				</p>
				<p>
					Dare Drop does not guarantee that Digital Content will be available at all
					times or at any given time. Dare Drop does not guarantee that we will continue
					to offer Digital Content for any particular length of time. Dare Drop may
					modify Digital Content at our sole discretion, and such modifications may make
					Digital Content more or less common, desirable, effective, or functional. Dare
					Drop may immediately suspend or terminate Digital Content for any or no reason,
					in our sole discretion, and without advance notice or liability. Dare Drop may
					increase the number of Points required to earn Rewards, withdraw any Reward,
					and restrict any Reward or Reward redemption at any time, even though such
					changes may affect the utility of Digital Content, or the ability to obtain
					certain Rewards. Users should not rely upon the continued availability of any
					Reward or Reward level, category, or tier.
				</p>
				<p>
					Dare Drop reserves the right to change and update our inventory of Digital
					Content without notice. Dare Drop reserves all rights, title, and interest in
					Digital Content and Digital Content inventories, and all associated copyrights,
					trademarks, and other intellectual property rights therein. Except as required
					by applicable law, any unused Digital Content inventory will be forfeited to
					Dare Drop upon termination of your Dare Drop account.
				</p>
				<p>
					Dare Drop reserves the right to deactivate any User from any Dare Drop Services
					for any reason or no reason. Once a User has been deactivated, any Digital
					Content accumulated by that User will be forfeited and may not be reinstated.
				</p>
			</React.Fragment>
		),
	},

	{
		id: 'section-7',
		title: '7. Dare Drop Sweepstakes',
		paragraph: (
			<span>
				This section covers the process and details surrounding who is eligible, how to
				participate, where to learn about prizes, and more.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					<strong>Official Rules</strong>
				</p>
				<p>
					<i>No purchase is necessary to enter and/or win any Dare Drop Sweepstakes. A
						purchase and/or payment will not improve your chances of winning.
					</i>
				</p>
				<p>
					1. Eligibility: A Dare Drop Sweepstakes is open to legal residents of the states and
					countries specified on the respective Drop page. Legal residents who accept, complete,
					and submit a Dare (or Dares) at www.daredrop.com are deemed to be Entrants and must be
					a minimum age as of their date of entry in any Dare Drop Sweepstakes in order to qualify.
					This age may vary and will be specified on the respective Drop page.
				</p>
				<p>
					This sweepstakes is subject to federal, state, and local laws and regulations and void
					where prohibited by law. Employees, subsidiaries, affiliates, suppliers, partners,
					advertising and promotion agencies, and directors of Dare Drop (collectively the “Dare
					Drop Employees”), as well as members of a Dare Drop Employees' immediate family and/or
					those living in the same household of Dare Drop Employees are ineligible to participate
					in any Dare Drop Sweepstakes.
				</p>
				<p>
					2. Sponsorship: A Dare Drop Sweepstakes may include a sponsor (“Sponsor”) that provides
					products and/or services as a prize to the Winner(s). Those prize(s) will be detailed on
					the respective Drop page(s). Dare Drop, Inc. and the Sponsor will be collectively referred
					to as “Hosts” in these Official Rules.

				</p>
				<p>
					Employees, subsidiaries, affiliates, suppliers, partners, advertising and promotion agencies,
					and directors of the Sponsor (collectively the “Sponsor Employees”), as well as members of a
					Sponsor Employees' immediate family and/or those living in the same household of Sponsor
					Employees are ineligible to participate in their respective Dare Drop Sweepstakes.
				</p>
				<p>
					3. Agreement to Rules: By entering any Dare Drop Sweepstakes, the Entrant (“You”) agrees to
					abide by these Official Rules and the Hosts' decisions, which are fully and unconditionally
					binding in all respects. The Hosts reserve the right to refuse, withdraw, or disqualify any
					entry at any time at their sole discretion.
				</p>
				<p>
					By entering this Sweepstakes, You represent and warrant that You are eligible to participate
					based on the eligibility requirements explained in these Official Rules. You also agree to
					accept the decisions of the Hosts as final and binding as it relates to the content of any
					Dare Drop Sweepstakes.
				</p>
				<p>
					4. Dare Drop Sweepstakes Entry Period: These Sweepstakes will have a start day and time, as
					well as an end day and time (“Entry Period”).  These will be provided on the respective Drop
					or Profile page(s). To be eligible for the Sweepstakes, entries must be received within the
					Entry Period specified on the respective page.
				</p>
				<p>
					5. How to Enter: Eligible entrants can participate in any Dare Drop Sweepstakes by submitting
					their Dare(s) at www.daredrop.com. As a participant, your entry must fully meet all Sweepstakes
					requirements, as specified in the Official Rules, in order to be eligible to win a prize.
					Incomplete entries or those that do not adhere to the Official Rules or specifications will
					be disqualified at the Hosts' sole discretion.
				</p>
				<p>
					6. Prizes: The Winner(s) of any Dare Drop Sweepstakes (the “Winner(s)”) will receive prizes
					from the Hosts. The actual/appraised prize value may differ at the time the prize is awarded.
					The prize(s) shall be determined solely by the Hosts. There shall be no cash or other prize
					substitution permitted, except at Dare Drop's and/or the Sponsor's discretion. The prize is
					non-transferable.
				</p>
				<p>
					The Winner(s), upon acceptance of the prize, is/are solely responsible for all expenses related
					to the prize not specified on the respective Drop page, including without limitation any and all
					local, state, and federal taxes. The Winner(s) shall not transfer assignment of the prize to others
					nor shall the Winner(s) request the cash equivalent or prize substitution.
				</p>
				<p>
					By accepting a prize, the selected Winner(s) grants permission for the Hosts to use the Winner(s)'
					likeness, entry, name, and user/screen name for purposes of advertising and trade without further
					compensation unless prohibited by Law.
				</p>
				<p>
					7. Odds: The total number of eligible entries received determines the odds of winning.
				</p>
				<p>
					8. Selection and Notification of Winner(s): The Winner(s) will be selected by either a
					random drawing, a community vote, a panel of judges, or other method under the supervision
					of the Hosts. The specific method will be detailed on the respective Drop page.
				</p>
				<p>
					The Hosts are not responsible for, nor shall have any liability for, any Winner's failure
					to receive notices of any kind. Nor shall the Hosts be liable for any Winner providing
					incorrect or otherwise non-functioning contact information.
				</p>
				<p>
					IF ANY WINNER VIOLATES ANY OF THESE OFFICIAL RULES, THE WINNER (AT DARE DROP'S AND/OR THE
					SPONSOR'S SOLE DISCRETION) WILL BE DISQUALIFIED, AND ALL PRIVILEGES AS WINNER WILL BE
					IMMEDIATELY TERMINATED.
				</p>
				<p>
					9. Rights Granted by You: By submitting an entry into this Sweepstakes, You understand and
					agree that the Hosts, any individual acting on the Hosts' behalf, and the licensee's successors,
					and assigns of the Hosts shall, where permitted by law, have the right to print, publish,
					broadcast, distribute, and use in any media known now or hereafter developed,
					worldwide, and without limitation, your entry, name, user/screen name, profile picture, voice,
					likeness, and/or statements about the any Dare Drop Sweepstakes for news, publicity, advertising,
					promotional purposes, trade, information, and public relations without any further notice, review,
					consent, compensation, or remuneration for six months. Dare Drop, Brands, or Partners
					will not be required to remove content posted prior to the expiration of that six months.
				</p>
				<p>
					You understand and agree your entry is an original work and does not violate any person's or
					entity's proprietary or intellectual property rights not included in the rights allowed for
					completing a Dare and/or Drop. You shall defend or settle against such claims at your sole
					expense and You shall indemnify, defend and hold harmless the Hosts from any suit or claim
					arising from such improper usage.
				</p>
				<p>
					10. Terms & Conditions: In their sole discretion, the Hosts reserves the right to modify,
					suspend, cancel, or terminate any Dare Drop Sweepstakes, should non-authorized human
					intervention, a bug or virus, fraud, or any other cause beyond Dare Drop's and/or the
					Sponsor's control, impact or corrupt the security, fairness, proper conduct, or administration
					of any Dare Drop Sweepstakes.
				</p>
				<p>
					The Hosts, in the event of any of the above issues, may determine the Winner(s) based on
					all eligible entries received prior to and/or after (if appropriate) the action taken by
					the Hosts. Individuals who tamper with, or attempt to tamper with, the operation or entry
					process of the Sweepstakes or website, or violates these Terms & Conditions will be disqualified
					by the Hosts.
				</p>
				<p>
					In their sole discretion, the Hosts have the right to maintain the integrity of the any Dare Drop
					Sweepstakes, to void votes or entries for any reason, including, but not limited to any disallowed
					entry methods that will result in votes/entries being voided (e.g. multiple entries from the same
					user unless specified on the respective Drop page, multiple entries from the same computer beyond
					the number allowed by the Sweepstakes rules, etc), or the use of bots, macros, scripts, or other
					technical means for entering.
				</p>
				<p>
					Attempts by any entrant to deliberately damage any website or undermine the legitimate operation
					of any Dare Drop Sweepstakes may be a violation of criminal and civil laws. If any such attempt
					is made, the Hosts reserves the right to seek damages to the fullest extent permitted by the Law.
				</p>
				<p>
					11. Limitation of Liability: Your entry into any Dare Drop Sweepstakes constitutes Your agreement
					to release and hold harmless the Hosts and its subsidiaries, representatives, affiliates, partners,
					advertising and promotion agencies, successors, agents, assigns, directors, employees, sponsors and
					officers against and from any and all claims, liability, illness, injury, death, litigation, loss,
					or damages that may occur, directly or indirectly from participation in the Sweepstakes and/or the
					a.) Winner(s) accepting, possessing, using, or misusing of any awarded prize or any portion thereof;
					b.) any type of technical failure; c.) the unavailability or inaccessibility of any transmission,
					phone, or Internet service; d.) unauthorized intervention in any part of the entry process or any
					Dare Drop Sweepstakes; e.) electronic error or human error in any Dare Drop Sweepstakes administration
					or the processing of entries.
				</p>
				<p>
					12. Disputes: THIS SWEEPSTAKES IS GOVERNED BY THE LAWS OF THE UNITED STATES OF AMERICA
					AND DELAWARE, WITHOUT RESPECT TO CONFLICT OF LAW DOCTRINES.
				</p>
				<p>
					The Entrant agrees, as a condition of participating in this promotion, that if any disputes cannot
					be resolved between the Entrant and the Hosts, and if causes of action arise out of, or are connected
					with, this Sweepstakes, they shall be individually resolved exclusively before a court located in
					Delaware and having jurisdiction, without resorting to any form of class action.
				</p>
				<p>
					Under no circumstances in any such dispute shall the Entrant be permitted to obtain awards for, and
					hereby waives all rights to, punitive, incidental, or consequential damages, including reasonable
					attorney's fees, other than participant's actual out-of-pocket expenses (i.e. costs associated with
					entering any Dare Drop Sweepstakes). The participant waives all rights to have damages multiplied
					or increased.
				</p>
				<p>
					13. Privacy Policy: Information submitted with an entry is subject to the Privacy Policy stated on
					Dare Drop's website. Visit www.daredrop.com/privacy-policy to read the Privacy Policy.
				</p>
				<p>
					14. Winner(s): Upon completion of any Dare Drop Sweepstakes and confirmation of the Winner(s), the
					Hosts will share the Winner(s) of each respective Drop on potential sites and services, like social
					media, websites, and email newsletters. The identity of the Winner(s) will not contain any personally
					identifiable information and will be strictly limited to the publicly available username and profile
					picture of the Winner(s).
				</p>
				<p>
					15. Social Media Channel Disclaimer: Unless otherwise specified on the respective Drop page, the Hosts
					are in no way sponsored by, endorsed by, administered by, or associated with Twitch, TikTok, or YouTube
					and no Dare Drop Sweepstakes should be construed as having such a relationship. The Hosts make no
					representations or warranties of any kind regarding any partnerships with Twitch, TikTok, or YouTube
					related to any Dare Drop Sweepstakes.
				</p>
			</React.Fragment>
		),
	},
	{
		id: 'section-8',
		title: '8. How Viewer Giveaways Work',
		paragraph: (
			<span>
				This section applies to when creators give viewers the opportunity to get
				Rewards by supporting the creator in some way (“Viewer Giveaways”). In
				short, when viewers do actions listed for a Viewer Giveaway, they gain
				entries into that giveaway. On the giveaway end date, the Winner is randomly
				selected and the Prize is to select a Reward from those available to the
				creator. Several Viewer Giveaways may happen at the same time, and the
				Rewards the creator has access to may change at any time as a result. The
				Prize expires in seven (7) days. Dare Drop reserves the right to delete
				accounts for any reason without recourse to protect the integrity of Viewer
				Giveaways.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					Viewer Giveaways have the same rules as the Dare Drop Sweepstakes, with
					these exceptions:
				</p>
				<p>
					If for any reason the viewer is determined to have multiple accounts, sell
					keys, or have any third party account connected to Dare Drop which Dare Drop
					determines to be a risk to the integrity of a giveaway, relevant accounts may
					be deleted by Dare Drop without recourse or compensation.
				</p>
				<p>
					Sweepstakes Section 5. In order to earn entries into a Viewer Giveaway, (i) a
					viewer must take the actions posted in the giveaway section; (ii) at the end
					date of the Viewer Giveaway, Dare Drop must have authenticated access to
					relevant platforms to verify these actions. Some of these actions are
					stateful, and the state must be true at the end date of the Viewer Giveaway
					in order for the entries for that action to be included in the giveaway. For
					instance, if a Viewer Giveaway action is to follow a creator on their Twitch
					channel, the viewer must be following that channel at the time of the end date
					for the campaign for them to claim the associated entry. As a participant,
					your entry must fully meet all Sweepstakes requirements, as specified in the
					Official Rules, in order to be eligible to win a prize. Incomplete entries or
					those that do not adhere to the Official Rules or specifications will be
					disqualified at the Hosts’ sole discretion.
				</p>
				<p>
					In addition to Sweepstakes Section 6, the Prize for Viewer Giveaways is the
					opportunity to select from the set of Rewards the relevant creator has
					available within seven (7) days. Other Viewer Giveaways granting opportunities
					for viewers to select from the set of Rewards may continue to run and the
					Rewards available may change as a result of viewers selecting Rewards on a
					“first come, first served” basis. Prize redemption expires seven (7) days from
					the Viewer Giveaway end date.
				</p>
				<p>
					Sweepstakes Section 8. Selection and Notification of Winner(s): The Winner(s)
					will be selected by a random drawing.

					The Hosts are not responsible for, nor shall have any liability for, any
					Winner’s failure to receive notices of any kind. Nor shall the Hosts be liable
					for any Winner providing incorrect or otherwise non-functioning contact
					information.

					IF ANY WINNER VIOLATES ANY OF THESE OFFICIAL RULES, THE WINNER (AT DARE DROP’S
					AND/OR THE SPONSOR'S SOLE DISCRETION) WILL BE DISQUALIFIED, AND ALL PRIVILEGES
					AS WINNER WILL BE IMMEDIATELY TERMINATED.
				</p>
			</React.Fragment>
		),
	},
	{
		id: 'section-9',
		title: '9. Terms Applicable to Developers, Brands, and Publishers',
		paragraph: (
			<span>
				This section applies to those who use the site to promote a brand, product,
				or service using a campaign. This includes all functionality you can use
				adding and publishing a campaign on our site, and funding Dares. This section
				does not exclude you from the rest of the terms, you are bound by those too.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					In order to use our services you must register and create an account. You
					must provide the background information on your game and organization laid
					out by our site to publish a campaign. We will review the information provided
					by you or any information that is publicly available. We reserve the right, in
					our sole discretion, to accept or reject your campaign and the use of our Site
					and/or Service. If your campaign is accepted, you will be allowed to make
					acceptable Content available to our community, such as the game pages, game
					keys, products, and dares you create to promote your brands, products, or
					services.
				</p>
				<p>
					<strong>Fees</strong>
				</p>
				<p>
					Developers, Brands, and Publishers may purchase one-time and subscription services.
					We reserve the right to modify our billing rates at any time upon thirty (30) days
					written notice by posting such fee changes to the Site or by email notification to
					you. You will be liable to pay such modified billing rates.
				</p>
				<p>
					If you purchase any service, you agree that our third party payment processors,
					such as Stripe and PayPal, may store your payment method. You expressly agree that
					we are authorized to charge you (i) a fee for any applicable service to which you
					have subscribed, billed on a basis of any applicable subscription period, (ii) any
					other fees for services you may purchase, (iii) any charges for use of the services
					in excess of the any limits placed on your use of the services (and you hereby
					consent to such charges and agree we are not required to notify you of any such
					charge in advance) and (iv) any applicable taxes in connection with your use of
					the services to the payment method you provide and to reimburse us for all
					collection costs and interest for any overdue amounts. If the payment method you
					provide expires and you do not provide new payment information or cancel your account,
					you authorize us to continue billing you and you agree to remain responsible for any
					uncollected fees.
				</p>
				<p>
					You take full responsibility for all taxes and fees associated with the services,
					including any sales tax related to any purchase or sale of services or goods added
					to the site by you. When purchasing or selling services or goods on the site, it is
					your responsibility to determine whether or not sales taxes apply to a transaction
					and to collect, report and remit the correct amounts to the appropriate authority.
				</p>
				<p>
					<strong>Cancellation Policy</strong>
				</p>
				<p>
					Unless otherwise set out in writing at the time of purchase with Dare Drop, fees
					for services will be billed in advance for each month and are non-refundable. There
					will be no refunds or credits for partial months of service or for periods in which
					your services are active but go unused.
				</p>
				<p>
					If a dare deliverer accepted a dare prior to your cancellation, you are liable for
					any reward listed for that dare if it is completed. Dare completion may happen up
					to thirty (30) days after your last subscription period ends. You remain liable
					through that period.
				</p>
			</React.Fragment>
		),
	},

	{
		title: '10. Stuff We Aren’t Responsible For',
		paragraph: (

			<span>
				We don’t oversee Dares’ performance, and we don’t mediate disputes between users.
			</span>

		),
		text: (
			<p>
				Dare Drop isn’t liable for any damages or losses related to your use of the Services. We
				don’t become involved in disputes between users, or between users and any third party
				relating to the use of the Services. We don’t oversee the performance or punctuality of
				Dares, and we don’t endorse any content users submit to the Site. When you use the
				Services, you release Dare Drop from claims, damages, and demands of every kind — known or
				unknown, suspected or unsuspected, Dsclosed or undisclosed — arising out of or in any way
				related to such disputes and the Services. All content you access through the Services is at
				your own risk. You’re solely responsible for any resulting damage or loss to any party.
			</p>
		),
	},
	{
		title: '11. Our Fees',
		paragraph: (

			<span>
				Fees are only charged on successfully funded Dares. We charge 10%, in addition to any fees
				from our payments partners.
			</span>

		),
		text: (
			<React.Fragment>
				<p>
					Creating an account on Dare Drop is free. If you create, fund or deliver on a Dare
					that is successfully funded, we (and our payment partners) collect fees. Our partners’
					fees may vary slightly based on your location.
				</p>
				<p>
					We will not collect any fees without giving you a chance to review and accept them. If our
					fees ever change, we’ll announce that on our Site. Some funds pledged by backers are
					collected by payment providers. Each payment provider is its own company, and Dare Drop
					isn’t responsible for its performance.
				</p>
				<p>
					You’re responsible for paying any additional fees or taxes associated with your use of
					Dare Drop.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '12. Other Websites',
		paragraph: (
			<span>
				If you follow a link to another website, what happens there is between you and them — not us.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					Dare Drop may contain links to other websites (for instance, Dare pages, and user
					profiles may link to other sites). When you access third-party websites, you
					do so at your own risk. We don’t control or endorse those sites.
				</p>
				<p>
					Dare Drop partners with other companies (such as <a href="https://paypal.com/" target="__blank">PayPal</a>) for
					payment processing. When you back or create a Dare, you’re also agreeing to the payment
					processor’s terms of service.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '13. Your Intellectual Property',
		paragraph: (
			<span>
				We don’t own the stuff you post on Dare Drop. But when you post it, you’re giving us
				permission to use or copy it however we need in order to run the site, or show people what’s
				happening on it. (We generally just use this to promote Dares and showcase our community
				on the website.) You’re responsible for the content you post, and you’re vouching to us that
				it’s all okay to use.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					Dare Drop, Developers, Brands, Publishers, and deliverers that use Dare Drop, don’t own content
					you submit to us (your “Content”). But we do need certain licenses from you in order to perform
					our Services. When you submit delivery on a Dare, submit a new Dare for review, or save
					content to a campaign you agree to these terms:
				</p>
				<ul className={classes.termsList}>
					<li>
						<b>We can use the content you’ve submitted</b>. You grant to us, Brands who have
						sponsored your Dares, and others acting on our behalf, the worldwide, non-exclusive,
						irrevocable, royalty-free, sublicensable, transferable right to use,
						exercise, commercialize, and exploit the copyright, publicity, trademark, and
						database rights with respect to your Content for six months. Dare Drop, Brands, or Partners
						will not be required to remove content posted prior to the expiration of that six months.
					</li>
					<li>
						<b> When Dare Drop or Brands use the content, we can make changes, like editing or
							translating it
						</b>.
						You grant us the right to edit, modify, reformat, excerpt, delete, or translate any of
						your Content.
					</li>
					<li>
						<b>
							You won’t submit Content you don’t hold the copyright for (unless you have permission)
						</b>
						. Your Content will not contain third-party copyrighted material, or material that is
						subject to other third-party proprietary rights, unless you have permission from the
						rightful owner of the material, or you are otherwise legally entitled to post the
						material (and to grant Dare Drop all the license rights outlined here).
					</li>
					<li>
						<b>Any royalties or licensing on your Content are your responsibility</b>. You will pay
						all royalties and other amounts owed to any person or entity based on your Content, or
						on Dare Drop’s hosting of that Content.
					</li>
					<li>
						<b>
							You promise that if we use your Content, we’re not violating anyone’s rights or
							copyrights
						</b>
						. If Dare Drop or its users exploit or make use of your submission in the ways
						contemplated in this agreement, you promise that this will not infringe or violate the
						rights of any third party, including (without limitation) any privacy rights, publicity
						rights, copyrights, contract rights, or any other intellectual property or proprietary
						rights.
					</li>
					<li>
						<b>You’re responsible for the stuff you post</b>. All information submitted to the Site,
						whether publicly posted or privately transmitted, is the sole responsibility of the
						person from whom that content originated.
					</li>
					<li>
						<b>We’re not responsible for mistakes in your content</b>. Dare Drop will not be liable
						for any errors or omissions in any content.
					</li>
				</ul>
			</React.Fragment>
		),
	},
	{
		title: '14. Dare Drop’s Intellectual Property',
		paragraph: (
			<span>
				The content on Dare Drop is protected in various ways. You do have the right to use it for
				certain personal purposes, but you can’t use it for anything commercial without getting
				permission first.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					Dare Drop’s Services are legally protected in various ways, including copyrights,
					trademarks, service marks, patents, trade secrets, and other rights and laws. You agree to
					respect all copyright and other legal notices, information, and restrictions contained in
					any content accessed through the Site. You also agree not to change, translate, or
					otherwise create derivative works of the Service.
				</p>
				<p>
					Dare Drop grants you a license to reproduce content from the Services for personal use
					only. This license covers both Dare Drop’s own protected content and user-generated
					content on the Site. (This license is worldwide, non-exclusive, non-sublicensable, and
					non-transferable.) If you want to use, reproduce, modify, distribute, or store any of this
					content for a commercial purpose, you need prior written permission from Dare Drop or the
					relevant copyright holder. A “commercial purpose” means you intend to use, sell, license,
					rent, or otherwise exploit content for commercial use, in any way.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '15. How We Deal with Copyright Issues',
		paragraph: (
			<span>
				We comply with
				the <a href="https://www.copyright.gov/legislation/dmca.pdf">
					Digital Millennium Copyright Act
					{/* eslint-disable-next-line react/jsx-closing-tag-location */}
				</a>.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					To learn more about how we deal with claims of copyright infringement, read our Copyright
					Policy. The Digital Millennium Copyright Act lays out a system of legal requirements for
					dealing with allegations of copyright infringement. Dare Drop complies with the DMCA, and
					we respond to notices of alleged infringement if they comply with the law and the
					requirements set forth in our <Link routeId={COPYRIGHT_POLICY_ROUTE_ID}>Copyright Policy</Link>.
					We reserve the right to delete or disable content alleged to be infringing, and to terminate
					accounts for repeat infringers. (We do this when appropriate and at our sole discretion.)
				</p>
				<p>
					If you’d like to submit a claim of copyright infringement, please visit our{' '}
					<Link routeId={COPYRIGHT_POLICY_ROUTE_ID}>Copyright Policy</Link>. Our designated agent
					for notice of alleged copyright infringement is:
				</p>
				<p>

					<span>Dare Drop</span>

					<br />

					<span>Attn: Copyright Office</span>

					<br />

					<span>PO Box 2021</span>

					<br />

					<span>St. Helena, CA 94574</span>

					<br />

					<span>
						<a href={`mailto:${EMAIL}`}>{EMAIL}</a>
					</span>

				</p>
			</React.Fragment>
		),
	},
	{
		title: '16. Deleting Your Account',
		paragraph: (
			<span>
				You can delete your account at any time. Deleting your account won’t automatically make some
				content you’ve already posted go away.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					You can delete your account at any time. Deleting your account won’t automatically make
					some content you’ve already posted go away.
				</p>
				<p>
					You can terminate your account at any time through your account settings. We may retain
					certain information as required by law or as necessary for our legitimate business
					purposes. All provisions of this agreement survive termination of an account, including
					our rights regarding any content you’ve already submitted to the Site. (For example, if
					you’ve created a Dare, deleting your account will not remove the Dare from the
					Site.) You can contact us at{' '}
					<a href={`mailto:${EMAIL}`}>{EMAIL}</a> for more
					information or to request Dare page deletion (this is not available in all
					circumstances).
				</p>
			</React.Fragment>
		),
	},
	{
		title: '17. Our Rights',
		paragraph: (
			<span>
				To operate, we need to be able to maintain control over what happens on our website. So in
				this Section, we reserve the right to make decisions to protect the health and integrity
				of our system. We don’t take these powers lightly, and we only use them when we absolutely
				have to.
			</span>
		),
		text: (
			<React.Fragment>
				<p>Dare Drop reserves these rights:</p>
				<ul className={classes.termsList}>
					<li>
						We can make changes to the Dare Drop Site and Services without notice or liability.
					</li>
					<li>
						We have the right to decide who’s eligible to use Dare Drop. We can cancel accounts or
						decline to offer our Services (especially if you’re abusing them). We can change our
						eligibility criteria at any time. If these things are prohibited by law where you live,
						then we revoke your right to use Dare Drop in that jurisdiction.
					</li>
					<li>
						We have the right to cancel any pledge to any Dare, at any time and for any reason.
					</li>
					<li>
						We have the right to reject, cancel, interrupt, remove, or suspend any Dare at any
						time and for any reason.
					</li>
				</ul>
				<p>
					Dare Drop is not liable for any damages as a result of any of these actions, and it is
					our policy not to comment on the reasons for any such action.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '18. Warranty Disclaimer',
		paragraph: (
			<span>
				We work hard to provide you with great services, but we can’t guarantee everything will
				always work perfectly. This site is presented as-is, without warranties.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					You use our Services solely at your own risk. They are provided to you “as is” and “as
					available” and <em>without warranty of any kind</em>, express or implied.
				</p>
				<p>
					DARE DROP SPECIFICALLY DISCLAIMS ANY AND ALL WARRANTIES AND CONDITIONS OF
					MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR A PARTICULAR PURPOSE, AND ANY
					WARRANTIES IMPLIED BY ANY COURSE OF DEALING, COURSE OF PERFORMANCE, OR USAGE OF TRADE. NO
					ADVICE OR INFORMATION (ORAL OR WRITTEN) OBTAINED BY YOU FROM Dare Drop SHALL CREATE ANY
					WARRANTY.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '19. Indemnification ',
		paragraph: (
			<span>
				If you do something on Dare Drop that winds up getting us sued, you have to help defend us.
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					If you do something that gets us sued, or break any of the promises you make in this
					agreement, you agree to defend, indemnify, and hold us harmless from all liabilities,
					claims, and expenses (including reasonable attorneys’ fees and other legal costs) that
					arise from or relate to your use or misuse of Dare Drop. We reserve the right to assume
					the exclusive defense and control of any matter otherwise subject to this indemnification
					clause, in which case you agree that you’ll cooperate and help us in asserting any
					defenses.
				</p>
				<p>
					If you do something that gets us sued, or break any of the promises you make in this
					agreement, you agree to defend, indemnify, and hold us harmless from all liabilities,
					claims, and expenses (including reasonable attorneys’ fees and other legal costs) that
					arise from or relate to your use or misuse of Dare Drop. We reserve the right to assume
					the exclusive defense and control of any matter otherwise subject to this indemnification
					clause, in which case you agree that you’ll cooperate and help us in asserting any
					defenses.
				</p>
			</React.Fragment>
		),
	},
	{
		title: '20. Limitation of Liability',
		paragraph: (
			<span>
				If something bad happens as a result of your use of Dare Drop, we’re not liable.
			</span>
		),
		text: (
			<p>
				To the fullest extent permitted by law, in no event will Dare Drop, its directors,
				employees, partners, suppliers, or content providers be liable for any indirect, incidental,
				punitive, consequential, special, or exemplary damages of any kind, including but not
				limited to damages (i) resulting from your access to, use of, or inability to access or use
				the Services; (ii) for any lost profits, data loss, or cost of procurement or substitute
				goods or services; or (iii) for any conduct of content of any third party on the Site.
			</p>
		),
	},
	{
		title: '21. Dispute Resolution and Governing Law',
		paragraph: (
			<span>
				We’re located in New York, and any disputes with us have to be handled in New York under New
				York State law.
			</span>
		),
		text: (
			<p>
				We at Dare Drop encourage you to contact us if you’re having an issue, before resorting to
				the courts. In the unfortunate situation where legal action does arise, these Terms (and all
				other rules, policies, or guidelines incorporated by reference) will be governed by and
				construed in accordance with the laws of the State of New York and the United States,
				without giving effect to any principles of conflicts of law, and without application of the
				Uniform Computer Information Transaction Act or the United Nations Convention of Controls
				for International Sale of Goods. You agree that Dare Drop and its Services are deemed a
				passive website that does not give rise to jurisdiction over Dare Drop or its parents,
				subsidiaries, affiliates, assigns, employees, agents, directors, officers, or shareholders,
				either specific or general, in any jurisdiction other than the State of New York. You agree
				that any action at law or in equity arising out of or relating to these Terms, or your use
				or non-use of Dare Drop, shall be filed only in the state or federal courts located in New
				York County in the State of New York, and you hereby consent and submit to the personal
				jurisdiction of these courts for the purposes of litigating any such action. You hereby
				irrevocably waive any right you may have to trial by jury in any dispute, action, or
				proceeding.
			</p>
		),
	},
	{
		title: '22. The Rest',
		paragraph: (
			<span>
				These are our official terms and our rules for how things work. (So if you ever see
				conflicting information about any of this stuff, the information here is the last word.)
			</span>
		),
		text: (
			<React.Fragment>
				<p>
					These Terms and the other material referenced in them are the entire agreement between you
					and Dare Drop with respect to the Services. They supersede all other communications and
					proposals (whether oral, written, or electronic) between you and Dare Drop with respect
					to the Services and govern our future relationship. If any provision of these Terms is
					found to be invalid under the law, that provision will be limited or eliminated to the
					minimum extent necessary so that the Terms otherwise will remain in full force and effect
					and enforceable. The failure of either you or Dare Drop to exercise any right provided
					for in these Terms in any way won’t be deemed a waiver of any other rights.
				</p>
				<p>
					These Terms are personal to you. You can’t assign them, transfer them, or sublicense them
					unless you get Dare Drop’s prior written consent. Dare Drop has the right to assign,
					transfer, or delegate any of its rights and obligations under these Terms without your
					consent. Dare Drop will provide you notice via email, written notice, or by conspicuously
					posting the notice on our Site.
				</p>
			</React.Fragment>
		),
	},
]

export default pageContent
